import {HttpErrorResponse} from '@angular/common/http';
import {CommonModule} from '@angular/common';
import {
  Component,
  DestroyRef,
  Inject,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  twoFactorMaxAttempts,
  TwoFactorAuthDialogData,
} from '@gatewee/services/two-factor-auth.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiDialogContext} from '@taiga-ui/core';
import {TuiTextfield} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {TuiInputPin} from '@taiga-ui/kit';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';

@Component({
  selector: 'gatewee-two-factor-auth',
  templateUrl: './two-factor-auth.component.html',
  styleUrls: ['./two-factor-auth.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    TuiInputPin,
    TuiTextfield,
    GateweeButtonComponent,
    GateweeIconComponent,
  ],
})
export class TwoFactorAuthComponent implements OnInit, OnDestroy {
  readonly pinControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.minLength(6)],
  });

  private lastSubmittedCode = '';
  private failedAttempts = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  timer: any;
  totalTime = 15 * 60;
  minutes = 15;
  seconds = 0;
  expired = false;
  submitting = false;
  verificationError: string | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      unknown,
      TwoFactorAuthDialogData<unknown>
    >,
  ) {}

  get data(): TwoFactorAuthDialogData<unknown> {
    return this.context.data;
  }

  get maxAttempts(): number {
    return this.data.maxAttempts ?? twoFactorMaxAttempts;
  }

  get remainingAttempts(): number {
    return Math.max(0, this.maxAttempts - this.failedAttempts);
  }

  get isOtpValid(): boolean {
    return (
      !this.submitting &&
      this.pinControl.valid &&
      this.pinControl.value.length === 6
    );
  }

  get pinInputDisabled(): boolean {
    return this.submitting || this.expired;
  }

  ngOnInit(): void {
    this.startTimer();

    this.pinControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(code => {
        if (this.submitting || this.expired) {
          return;
        }
        if ((code || '').length !== 6) {
          return;
        }
        if (code === this.lastSubmittedCode) {
          return;
        }

        this.lastSubmittedCode = code;
        this.submit();
      });
  }

  startTimer(): void {
    this.minutes = Math.floor(this.totalTime / 60);
    this.seconds = this.totalTime % 60;
    this.expired = false;

    this.timer = setInterval(() => {
      if (this.totalTime > 0) {
        this.totalTime--;
        this.minutes = Math.floor(this.totalTime / 60);
        this.seconds = this.totalTime % 60;
      } else {
        this.expired = true;
        clearInterval(this.timer);
      }
    }, 1000);
  }

  resendCode(): void {
    this.totalTime = 15 * 60;
    this.failedAttempts = 0;
    this.verificationError = null;
    this.pinControl.enable({emitEvent: false});
    this.pinControl.setValue('');
    this.lastSubmittedCode = '';
    this.startTimer();
  }

  submit(): void {
    const code = this.pinControl.value;
    const verify = this.data.verify;

    if (!verify) {
      this.context.completeWith(code);
      return;
    }

    if (this.submitting || this.expired || code.length !== 6) {
      return;
    }

    this.submitting = true;
    this.verificationError = null;
    this.pinControl.disable({emitEvent: false});

    verify(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.context.completeWith(result);
        },
        error: (err: unknown) => {
          this.submitting = false;
          this.pinControl.enable({emitEvent: false});

          if (this.isInvalid2FaError(err)) {
            this.failedAttempts++;
            this.lastSubmittedCode = '';
            this.pinControl.setValue('', {emitEvent: false});
            this.pinControl.setErrors({invalidCode: true});
            this.pinControl.markAsTouched();

            if (this.failedAttempts >= this.maxAttempts) {
              this.context.completeWith('max_attempts');
              return;
            }

            this.verificationError = this.translate.instant(
              'common.twoFactor.invalidCodeRemaining',
              {remaining: this.remainingAttempts},
            );
            return;
          }

          this.context.completeWith(err);
        },
      });
  }

  closeModal(): void {
    if (this.submitting) {
      return;
    }
    this.context.completeWith(undefined);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private isInvalid2FaError(err: unknown): boolean {
    return (
      err instanceof HttpErrorResponse &&
      err.status === 403 &&
      err.error?.message === 'Invalid 2FA code'
    );
  }
}
